import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Header from '../../components/layout/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const BookingAction = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const bookingId = searchParams.get('bookingId');
  const action = searchParams.get('action');
  // Decode token (it's URL encoded in the WhatsApp link)
  const rawToken = searchParams.get('token');
  
  // Try to decode the token, but handle errors gracefully
  let token = null;
  try {
    if (rawToken) {
      // Try decoding once
      token = decodeURIComponent(rawToken);
      // If it's still encoded (contains %), try again
      if (token.includes('%')) {
        token = decodeURIComponent(token);
      }
    }
  } catch (e) {
    console.error('❌ Error decoding token:', e);
    // If decoding fails, use raw token as fallback
    token = rawToken;
  }
  
  console.log('🔗 Booking action page loaded:', {
    bookingId,
    action,
    tokenLength: token?.length,
    rawTokenLength: rawToken?.length,
    tokenPreview: token ? token.substring(0, 30) + '...' : null,
    rawTokenPreview: rawToken ? rawToken.substring(0, 30) + '...' : null,
    url: window.location.href,
    searchParams: Object.fromEntries(searchParams.entries())
  });

  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);
  const [bookingSummary, setBookingSummary] = useState(null);
  const [photographerSummary, setPhotographerSummary] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [confirmed, setConfirmed] = useState(false);
  const [confirmedAction, setConfirmedAction] = useState(null);

  // Initialize: Fetch booking summary
  useEffect(() => {
    if (!bookingId || !action || !token) {
      setError('الرابط غير صالح');
      setLoading(false);
      return;
    }

    if (action !== 'accept' && action !== 'reject') {
      setError('الرابط غير صالح');
      setLoading(false);
      return;
    }

    loadBookingSummary();
  }, [bookingId, action, token]);

  // Countdown timer
  useEffect(() => {
    if (secondsLeft <= 0 || confirmed) return;

    const timer = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [secondsLeft, confirmed]);

  const loadBookingSummary = async () => {
    try {
      setLoading(true);
      setError(null);

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) {
        console.error('❌ VITE_SUPABASE_URL is not defined');
        setError('خطأ في إعدادات التطبيق');
        setLoading(false);
        return;
      }

      console.log('📡 Calling booking-action-init:', {
        url: `${supabaseUrl}/functions/v1/booking-action-init`,
        bookingId,
        action,
        tokenLength: token?.length,
        tokenPreview: token ? token.substring(0, 30) + '...' : null,
        rawTokenPreview: rawToken ? rawToken.substring(0, 30) + '...' : null
      });

      const requestBody = {
        bookingId,
        action,
        token,
      };
      
      console.log('📤 Request body:', {
        bookingId,
        action,
        tokenLength: token?.length,
        hasToken: !!token
      });

      const response = await fetch(`${supabaseUrl}/functions/v1/booking-action-init`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      console.log('📡 Response status:', response.status);
      console.log('📡 Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { message: errorText };
        }
        
        console.error('❌ Edge Function error response:', {
          status: response.status,
          statusText: response.statusText,
          body: errorData,
          rawBody: errorText
        });
        
        // Show more specific error message
        const errorMessage = errorData.message || errorData.debug || 'فشل تحميل بيانات الحجز. يرجى المحاولة مرة أخرى.';
        setError(errorMessage);
        setLoading(false);
        return;
      }

      const data = await response.json();
      console.log('📡 Response data:', data);

      if (!data.ok) {
        console.error('❌ Edge Function returned error:', data);
        setError(data.message || data.debug || 'الرابط غير صالح');
        setLoading(false);
        return;
      }

      setBookingSummary(data.bookingSummary);
      setPhotographerSummary(data.photographerSummary);
      setSecondsLeft(data.secondsLeft || 0);
      setLoading(false);
    } catch (err) {
      console.error('❌ Error loading booking summary:', err);
      setError('حدث خطأ أثناء تحميل بيانات الحجز. يرجى التحقق من الاتصال بالإنترنت.');
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    try {
      setConfirming(true);
      setError(null);

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) {
        console.error('❌ VITE_SUPABASE_URL is not defined');
        setError('خطأ في إعدادات التطبيق');
        setConfirming(false);
        return;
      }

      console.log('📡 Calling booking-action-confirm:', {
        url: `${supabaseUrl}/functions/v1/booking-action-confirm`,
        bookingId,
        action,
        tokenLength: token?.length
      });

      const response = await fetch(`${supabaseUrl}/functions/v1/booking-action-confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bookingId,
          action,
          token,
        }),
      });

      console.log('📡 Response status:', response.status);
      console.log('📡 Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Edge Function error response:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText
        });
        setError('فشل تأكيد العملية. يرجى المحاولة مرة أخرى.');
        setConfirming(false);
        return;
      }

      const data = await response.json();
      console.log('📡 Response data:', data);

      if (!data.ok) {
        console.error('❌ Edge Function returned error:', data);
        setError(data.message || 'فشل تأكيد العملية');
        setConfirming(false);
        return;
      }

      setConfirmed(true);
      setConfirmedAction(action);
      setConfirming(false);
    } catch (err) {
      console.error('❌ Error confirming action:', err);
      setError('حدث خطأ أثناء تأكيد العملية. يرجى التحقق من الاتصال بالإنترنت.');
      setConfirming(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'غير محدد';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'غير محدد';
    return timeString;
  };

  const formatDuration = (duration) => {
    if (!duration) return 'غير محدد';
    return duration;
  };

  const formatPrice = (price) => {
    if (!price) return 'غير محدد';
    return `${price} ر.س`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50">
        <Header />
        <div className="pt-20 pb-20 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">جاري التحميل...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !bookingSummary) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50">
        <Header />
        <div className="pt-20 pb-20">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-2xl">
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <Icon name="AlertCircle" size={48} className="mx-auto mb-4 text-destructive" />
              <h2 className="text-2xl font-bold text-slate-900 mb-4">خطأ</h2>
              <p className="text-slate-600 mb-6">{error}</p>
              <Button onClick={() => navigate('/')} variant="default">
                العودة للصفحة الرئيسية
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (confirmed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50">
        <Header />
        <div className="pt-20 pb-20">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-2xl">
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <Icon 
                name={confirmedAction === 'accept' ? 'CheckCircle2' : 'XCircle'} 
                size={64} 
                className={`mx-auto mb-4 ${confirmedAction === 'accept' ? 'text-success' : 'text-destructive'}`} 
              />
              <h2 className="text-2xl font-bold text-slate-900 mb-4">
                {confirmedAction === 'accept' ? 'تم قبول الطلب' : 'تم رفض الطلب'}
              </h2>
              <p className="text-slate-600 mb-6">
                {confirmedAction === 'accept' 
                  ? 'تم قبول طلب الحجز بنجاح. سيتم إشعار العميل.' 
                  : 'تم رفض طلب الحجز. سيتم البحث عن مصور آخر.'}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button onClick={() => navigate('/photographer-dashboard')} variant="default">
                  فتح لوحة المصور
                </Button>
                <Button onClick={() => navigate('/')} variant="outline">
                  العودة للصفحة الرئيسية
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isAccept = action === 'accept';
  const actionTitle = isAccept ? 'تأكيد قبول الطلب' : 'تأكيد رفض الطلب';
  const actionButtonText = isAccept ? 'تأكيد القبول' : 'تأكيد الرفض';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50">
      <Header />
      <div className="pt-20 pb-20">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-2xl">
          <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
            {/* Header */}
            <div className="text-center mb-6">
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                isAccept ? 'bg-success/10' : 'bg-destructive/10'
              }`}>
                <Icon 
                  name={isAccept ? 'CheckCircle2' : 'XCircle'} 
                  size={32} 
                  className={isAccept ? 'text-success' : 'text-destructive'} 
                />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                {actionTitle}
              </h1>
              {secondsLeft > 0 && (
                <p className="text-sm text-slate-600">
                  متبقي: {Math.floor(secondsLeft / 60)}:{(secondsLeft % 60).toString().padStart(2, '0')}
                </p>
              )}
              {secondsLeft === 0 && (
                <p className="text-sm text-destructive font-medium">
                  انتهت مدة الرد على الطلب
                </p>
              )}
            </div>

            {error && (
              <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            {/* Booking Summary */}
            {bookingSummary && (
              <div className="space-y-4 mb-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">تفاصيل الحجز</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <Icon name="MapPin" size={20} className="text-primary mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-500 mb-1">المدينة</p>
                      <p className="text-sm font-medium text-slate-900">
                        {bookingSummary.city || 'غير محدد'}
                        {bookingSummary.district && ` - ${bookingSummary.district}`}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Icon name="Calendar" size={20} className="text-primary mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-500 mb-1">التاريخ</p>
                      <p className="text-sm font-medium text-slate-900">
                        {formatDate(bookingSummary.date)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Icon name="Clock" size={20} className="text-primary mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-500 mb-1">الوقت</p>
                      <p className="text-sm font-medium text-slate-900">
                        {formatTime(bookingSummary.time)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Icon name="Timer" size={20} className="text-primary mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-500 mb-1">المدة</p>
                      <p className="text-sm font-medium text-slate-900">
                        {formatDuration(bookingSummary.duration)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Icon name="Camera" size={20} className="text-primary mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-500 mb-1">نوع التصوير</p>
                      <p className="text-sm font-medium text-slate-900">
                        {bookingSummary.photographyType || 'غير محدد'}
                      </p>
                    </div>
                  </div>

                  {bookingSummary.shootingMethod && (
                    <div className="flex items-start gap-3">
                      <Icon name="Video" size={20} className="text-primary mt-0.5" />
                      <div>
                        <p className="text-xs text-slate-500 mb-1">وسيلة التصوير</p>
                        <p className="text-sm font-medium text-slate-900">
                          {bookingSummary.shootingMethod}
                        </p>
                      </div>
                    </div>
                  )}

                  {bookingSummary.totalPrice && (
                    <div className="flex items-start gap-3">
                      <Icon name="DollarSign" size={20} className="text-primary mt-0.5" />
                      <div>
                        <p className="text-xs text-slate-500 mb-1">المبلغ</p>
                        <p className="text-sm font-medium text-slate-900">
                          {formatPrice(bookingSummary.totalPrice)}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {bookingSummary.equipment && Array.isArray(bookingSummary.equipment) && bookingSummary.equipment.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <p className="text-xs text-slate-500 mb-2">المعدات المطلوبة</p>
                    <div className="flex flex-wrap gap-2">
                      {bookingSummary.equipment.map((item, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {(() => {
                  const { deliverableMode, photosCount, videosCount, deliverableLabel } = bookingSummary;
                  const hasDeliverables = deliverableMode !== 'unspecified' && (photosCount > 0 || videosCount > 0 || deliverableLabel);
                  if (!hasDeliverables && deliverableMode !== 'unspecified') return null;
                  const label = deliverableMode === 'unspecified'
                    ? 'غير محدد (يقدرها المصور)'
                    : deliverableLabel || [
                        photosCount > 0 ? `${photosCount} صورة` : null,
                        videosCount > 0 ? `${videosCount} فيديو` : null,
                      ].filter(Boolean).join(' + ');
                  if (!label) return null;
                  return (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <p className="text-xs text-slate-500 mb-2">عدد المخرجات المطلوبة</p>
                      <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                        {label}
                      </span>
                    </div>
                  );
                })()}

                {/* Location Section */}
                {bookingSummary.locationDetails && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <Icon name="MapPin" size={18} className="text-primary" />
                      موقع الجلسة
                    </h3>
                    {bookingSummary.locationDetails.lat && bookingSummary.locationDetails.lng ? (
                      <div className="space-y-3">
                        <a
                          href={`https://www.google.com/maps?q=${bookingSummary.locationDetails.lat},${bookingSummary.locationDetails.lng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block w-full"
                        >
                          <Button
                            variant="outline"
                            fullWidth
                            iconName="ExternalLink"
                            iconPosition="right"
                            className="border-primary text-primary hover:bg-primary hover:text-white transition-colors"
                          >
                            فتح الموقع على الخريطة
                          </Button>
                        </a>
                        <div className="text-sm text-slate-600 space-y-1">
                          {bookingSummary.locationDetails.city && (
                            <p className="flex items-center gap-2">
                              <Icon name="MapPin" size={14} className="text-slate-400" />
                              <span className="font-medium">المدينة:</span> {bookingSummary.locationDetails.city}
                            </p>
                          )}
                          {bookingSummary.locationDetails.district && (
                            <p className="flex items-center gap-2">
                              <Icon name="MapPin" size={14} className="text-slate-400" />
                              <span className="font-medium">الحي:</span> {bookingSummary.locationDetails.district}
                            </p>
                          )}
                          {bookingSummary.locationDetails.address_text && (
                            <p className="text-slate-700 mt-2 pt-2 border-t border-slate-200">
                              {bookingSummary.locationDetails.address_text}
                            </p>
                          )}
                        </div>
                      </div>
                    ) : bookingSummary.locationDetails.address_text ? (
                      <div className="text-sm text-slate-700">
                        <p>{bookingSummary.locationDetails.address_text}</p>
                        {bookingSummary.locationDetails.city && (
                          <p className="mt-2 text-slate-600">
                            <span className="font-medium">المدينة:</span> {bookingSummary.locationDetails.city}
                          </p>
                        )}
                        {bookingSummary.locationDetails.district && (
                          <p className="text-slate-600">
                            <span className="font-medium">الحي:</span> {bookingSummary.locationDetails.district}
                          </p>
                        )}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            )}

            {/* Action Button */}
            <div className="flex flex-col gap-4">
              <Button
                onClick={handleConfirm}
                disabled={confirming || secondsLeft === 0}
                loading={confirming}
                variant={isAccept ? 'default' : 'destructive'}
                fullWidth
                size="lg"
                iconName={isAccept ? 'CheckCircle2' : 'XCircle'}
                iconPosition="right"
                className={isAccept 
                  ? 'bg-gradient-to-r from-success to-green-600 hover:from-success/90 hover:to-green-700 text-white' 
                  : 'bg-gradient-to-r from-destructive to-red-600 hover:from-destructive/90 hover:to-red-700 text-white'
                }
              >
                {actionButtonText}
              </Button>

              {secondsLeft === 0 && (
                <p className="text-sm text-center text-slate-600">
                  انتهت مدة الرد على الطلب. يرجى التواصل مع الإدارة.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingAction;
